<script lang="ts">
  import type { UserTopicStore } from 'shared/src/user/userKnowledge/user.topic.store';
  import { isNumber } from 'shared/src/utils/isNumber';
  import { autorun } from 'mobx';
  import { onDestroy } from 'svelte';
  import SkillTitle from '../SkillTitle.svelte';
  import { fly } from 'svelte/transition';
  import { bounceOut } from 'svelte/easing';

  export let topic: UserTopicStore;

  let skillsStats: ReturnType<typeof getSkillsStats>;
  let topicStats: ReturnType<typeof getTopicStats>;

  const textAnchorRightCutoff = 30;

  const destroy = autorun(() => {
    topicStats = getTopicStats();
    skillsStats = getSkillsStats();
  });

  onDestroy(destroy);

  function randomDuration(duration = 1000, variance = 0.2) {
    const newDuration = duration + duration * Math.random() * variance - (duration * variance) / 2;
    return Math.abs(newDuration);
  }

  function getSkillsStats() {
    return topic.visibleSkills.map((skill) => {
      const speedRequired = skill.isSpeedRequired;
      const { speedScore, accuracyScore, questionsScore, numQuestions } = skill;
      const speed = isNumber(skill.speed) ? Math.round(skill.speed * 100) / 100 : null;
      const title = skill.title;
      const minQuestions = skill.definition.minQuestions;

      return {
        id: skill.textId,
        skill,
        requiresSvg: skill.definition.hasOwnProperty('simple'),
        showOverall: false,
        title,
        speed,
        requiredSpeed: skill.requiredSpeed,
        speedScore: isNumber(speedScore) ? Math.round(speedScore) : 0,
        accuracyScore: Math.round(accuracyScore),
        questionsScore: Math.round(questionsScore),
        numQuestions,
        minQuestions,
        speedRequired,
      };
    });
  }

  function getTopicStats() {
    const { speedScore, accuracyScore, questionsScore, numQuestionsCounted: numQuestions } = topic;
    const minQuestions = topic.minQuestionsRequired;
    const speed = isNumber(topic.speed) ? Math.round(topic.speed * 100) / 100 : NaN;
    const speedRequired = topic.isSpeedRequired;

    return {
      id: topic.textId,
      topic,
      overallScore: topic.score,
      showOverall: true,
      speed,
      speedScore: isNumber(speedScore) ? Math.round(speedScore) : 0,
      accuracyScore: Math.round(accuracyScore),
      questionsScore: Math.round(questionsScore),
      numQuestions,
      minQuestions,
      speedRequired,
      requiredSpeed: topic.requiredSpeed,
      speedThatIsRequired: topic.speed,
      topicTitle: topic.title,
      title: `Overall: ${topic.title}`,
      requiresSvg: false,
    };
  }

  let chart: ReturnType<typeof getChart>;
  let rowsTaken: number;
  $: {
    chart = getChart(topicStats, skillsStats);
  }

  function getChart(topicS: typeof topicStats, rows: typeof skillsStats) {
    const startingRow = 4;
    rowsTaken = startingRow;
    return [topicS, ...rows].map((row, index) => {
      if (row.requiresSvg) rowsTaken += 1.75; // needs bigger buffer.
      const offsetForTopicOverallScore = row.showOverall ? 1 : 0;
      const rowWithStartingRow = { startingRow: rowsTaken, offsetForTopicOverallScore, ...row };
      rowsTaken += row.speedRequired ? 5 : 4;
      rowsTaken += offsetForTopicOverallScore;
      if (index === 0) rowsTaken++;
      return rowWithStartingRow;
    });
  }
</script>

<div class="user-skills chart">
  <div class="user-skills chart legend md:flex md:flex-wrap w-fit mx-auto">
    <span
      aria-hidden
      class="user-skills chart legend item overall md:mx-2 md:inline-block block w-auto"
      >Overall</span
    >
    <span
      aria-hidden
      class="user-skills chart legend item accuracy md:mx-2 md:inline-block block w-auto"
      >Accuracy</span
    >
    <span
      aria-hidden
      class="user-skills chart legend item num-questions md:mx-2 md:inline-block block w-auto"
      ># of Questions</span
    >
    {#if topic.isSpeedRequired}
      <span class="user-skills chart legend item speed md:mx-2 md:inline-block block w-auto"
        >Speed</span
      >
    {/if}
  </div>
  <svg width="100%" height="{rowsTaken * 1.5}em" xmlns="http://www.w3.org/2000/svg">
    <title>{topicStats.title} Progress</title>
    <desc>Chart of progress on component skills</desc>
    <defs>
      <filter x="0" y="0" width="1" height="1" id="solid">
        <feFlood flood-color="#FFFFFB" />
        <feComposite in="SourceGraphic" />
      </filter>
      <filter id="text-shadow">
        <feDropShadow dx="1" dy="1" stdDeviation="1" />
      </filter>
    </defs>
    <!-- the chart -->
    <rect
      x="0"
      y="0"
      width="100%"
      height="100%"
      stroke="none"
      stroke-width="1"
      fill="none"
      class="background"
    />
    <svg x="3%" y="0">
      <g transform="scale(0.94, 1)" class="chart">
        <!-- the grid -->
        <g stroke="#E5E5E5" stroke-width="1" font-size="1em" class="chart grid-lines">
          <line x1="0%" x2="0%" y1="1em" y2="100%" />
          <line x1="20%" x2="20%" y1="1em" y2="100%" />
          <line x1="40%" x2="40%" y1="1em" y2="100%" />
          <line x1="60%" x2="60%" y1="1em" y2="100%" />
          <line x1="80%" x2="80%" y1="1em" y2="100%" />
          <line x1="100%" x2="100%" y1="1em" y2="100%" />
        </g>

        <!-- the labels -->
        <g stroke="none" fill="#999999" font-size="1.5em" class="chart grid-text">
          <text aria-hidden x="0%" y="1em" text-anchor="start"> 0 </text>
          <text aria-hidden x="20%" y="1em" text-anchor="middle"> 20 </text>
          <text aria-hidden x="40%" y="1em" text-anchor="middle"> 40 </text>
          <text aria-hidden x="60%" y="1em" text-anchor="middle"> 60 </text>
          <text aria-hidden x="80%" y="1em" text-anchor="middle"> 80 </text>
          <text aria-hidden x="100%" y="1em" text-anchor="end"> 100 </text>
        </g>
        {#each chart as row (row.id)}
          <g font-size="1.5em" class="chart values">
            <!-- Row Title -->
            {#if row.requiresSvg && 'skill' in row}
              <foreignObject x="0" y="{row.startingRow - 2.25}em" width="100%" height="20%">
                <!--
              In the context of SVG embedded in an HTML document, the XHTML
              namespace could be omitted, but it is mandatory in the
              context of an SVG document.

              The overflow:auto is necessary here to reveal relatively positioned
              elements in time signature classes. It's a hack, but it works.
            -->
                <div style="overflow: auto;">
                  <SkillTitle skill={row.skill} color="#333333" />
                  {!row.skill.isRequired ? '(Optional)' : ''}
                </div>
              </foreignObject>
            {:else}
              <text
                x="0"
                y="{row.startingRow - 0.25}em"
                fill="#666666"
                class="chart values text-label"
              >
                {row.title}
                {'skill' in row && !row.skill.isRequired ? ' (Optional)' : ''}
                {'topic' in row && !row.topic.isRequired ? ' (Optional)' : ''}
              </text>
            {/if}
            <!-- /Row Title -->

            {#if row.showOverall && 'overallScore' in row}
              <!-- Row overall: Only visible on topic header -->
              <rect
                x="0"
                y="{row.startingRow + row.offsetForTopicOverallScore - 1}em"
                width="{row.overallScore || 0.25}%"
                height="1em"
                class="fadeInLeft animated chart values bar overall"
                in:fly={{
                  x: -200,
                  y: 0,
                  duration: randomDuration(),
                  easing: bounceOut,
                  opacity: 1,
                }}
              >
                <title>Overall score: {row.overallScore.toFixed(0)}%</title>
              </rect>

              <text
                x="{row.overallScore + (row.overallScore > textAnchorRightCutoff ? -1 : 0.5)}%"
                y="{row.startingRow + row.offsetForTopicOverallScore - 1 + 0.85}em"
                pointer-events="none"
                text-anchor={row.overallScore > textAnchorRightCutoff ? 'end' : 'start'}
                class="percent-label accuracy"
                in:fly={{
                  x: -200,
                  y: 0,
                  duration: randomDuration(),
                  easing: bounceOut,
                  opacity: 1,
                }}
              >
                {row.overallScore.toFixed(0)}%
              </text>
            {/if}

            <!-- Row Accuracy -->
            <rect
              x="0"
              y="{row.startingRow + row.offsetForTopicOverallScore}em"
              width="{row.accuracyScore || 0.25}%"
              height="1em"
              class="fadeInLeft animated chart values bar accuracy"
              in:fly={{ x: -200, y: 0, duration: randomDuration(), easing: bounceOut, opacity: 1 }}
            >
              <title>{row.title} accuracy {row.accuracyScore}%</title>
            </rect>
            <text
              x="{row.accuracyScore + (row.accuracyScore > textAnchorRightCutoff ? -1 : 0.5)}%"
              y="{row.startingRow + row.offsetForTopicOverallScore + 0.85}em"
              pointer-events="none"
              text-anchor={row.accuracyScore > 30 ? 'end' : 'start'}
              class="percent-label accuracy"
              in:fly={{ x: -200, y: 0, duration: randomDuration(), easing: bounceOut, opacity: 1 }}
            >
              {row.accuracyScore}%
            </text>

            <!-- Row Number of Questions -->
            <rect
              x="0"
              y="{row.startingRow + row.offsetForTopicOverallScore + 1}em"
              width="{row.questionsScore || 0.25}%"
              height="1em"
              class="fadeInLeft animated chart values bar num-questions"
              in:fly={{ x: -200, y: 0, duration: randomDuration(), easing: bounceOut, opacity: 1 }}
            >
              <title
                >{row.numQuestions} answered, {row.questionsScore >= 100
                  ? 'mastered'
                  : row.minQuestions - row.numQuestions + ' more required'}</title
              >
            </rect>
            <text
              x="{row.questionsScore + (row.questionsScore > textAnchorRightCutoff ? -1 : 0.5)}%"
              y="{row.startingRow + row.offsetForTopicOverallScore + 1.85}em"
              pointer-events="none"
              text-anchor={row.questionsScore > textAnchorRightCutoff ? 'end' : 'start'}
              class="percent-label questions"
              in:fly={{ x: -200, y: 0, duration: randomDuration(), easing: bounceOut, opacity: 1 }}
            >
              {row.numQuestions}/{row.minQuestions}
            </text>

            <!-- Row Speed -->
            {#if row.speedRequired && 'speedScore' in row}
              <rect
                x="0"
                y="{row.startingRow + row.offsetForTopicOverallScore + 2}em"
                width="{row.speedScore || 0.25}%"
                height="1em"
                class="fadeInLeft animated chart values bar speed"
                in:fly={{
                  x: -200,
                  y: 0,
                  duration: randomDuration(),
                  easing: bounceOut,
                  opacity: 1,
                }}
              >
                <title>
                  {#if row.speed === null}
                    Not begun
                  {:else}
                    Average speed {row.speed}s, {(row.speedScore || 0) >= 100
                      ? 'mastered'
                      : 100 - (row.speedScore || 0) + '% too slow'}
                  {/if}
                </title>
              </rect>

              <text
                x="{row.speedScore + (row.speedScore > textAnchorRightCutoff ? -1 : 0.5)}%"
                y="{row.startingRow + row.offsetForTopicOverallScore + 2.85}em"
                pointer-events="none"
                text-anchor={row.speedScore > textAnchorRightCutoff ? 'end' : 'start'}
                class="percent-label questions"
                in:fly={{
                  x: -200,
                  y: 0,
                  duration: randomDuration(),
                  easing: bounceOut,
                  opacity: 1,
                }}
              >
                {#if row.requiredSpeed !== null}
                  {@const speedRequired = row.speedRequired ? `/${Math.round(row.requiredSpeed*10)/10}s` :''}
                  {row.speed}s{speedRequired}
                {/if}
              </text>
            {/if}
          </g>
        {/each}
      </g>
    </svg>
  </svg>
</div>

<style lang="postcss">
  .user-skills {
    &.chart {
      line-height: 1em;
      padding-bottom: 25px;

      &.legend {
        font-size: 1.25em;
        padding: 10px 0px;
        /* text-align: center; */
        /* justify-content: space-between; */
        &.item {
          /* display: inline-block; */
          /* &:not(:first-child) {
            padding-left: 2.25em;
          } */
          &:before {
            content: '';
            display: inline-block;
            width: 1.5em;
            height: 1em;
            margin-right: 5px;
            transform: translateY(0.125em);
          }
          &.accuracy {
            &:before {
              @apply bg-cyan-600;
              /* background: #2d97de; */
            }
          }
          &.speed {
            &:before {
              @apply bg-red-300;
              /* background: #ea4b35; */
            }
          }
          &.num-questions {
            &:before {
              @apply bg-green;
              /* background: #81c800; */
            }
          }
          &.overall {
            &:before {
              @apply bg-orange;
              /* background: #ff9703; */
            }
          }
        }
      }

      .chart {
        .percent-label {
          fill: white;
          text-shadow: 2px 2px 2.5px #333333;
          &.accuracy {
            /* // stroke: #81c800; */
            /* // stroke-width: 1px; */
          }
        }

        /*
    green[400],
    yellow[500],
    orange[600],
    red[200],
*/

        &.values {
          /* font-size: 1.5em; */
          &.text-label {
            fill: #666666;
          }

          &.bar {
            &.overall {
              /* fill: #ff9703; */
              @apply fill-orange;
            }

            &.accuracy {
              @apply fill-cyan-600;
              /* @apply fill-green; */
              /* fill: #2d97de; */
            }

            &.num-questions {
              /* fill: #81c800; */
              @apply fill-green;
              /* @apply fill-cyan-600; */
            }

            &.speed {
              /* fill: #ea4b35; */
              @apply fill-red-300;
            }
          }
        }
      }
    }
  }
</style>
